import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {DataPolicyCollaborator, isActorCollaborator, Owner, OwnerInfo} from 'auth/PermissionConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {EntityType, isUser} from 'constants/UserConstants';
import {getPartyDisplayString} from 'parties/GetPartyDisplayString';
import {SavedSearchPermissionLevel, setDataPolicyId} from 'search/saved-searches/SavedSearchSharingUtil';
import {FONT_FAMILY_PROXIMA} from 'styles/FontConstants';
import {HasAvatar} from 'ui-library/avatar/Avatar';
import {AvatarShape, AvatarSize} from 'ui-library/avatar/AvatarConstants';
import {AvatarWithName} from 'ui-library/avatar/AvatarWithName';
import Tooltip, {TooltipPlacement} from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY, GRAY_DARKER, NEAR_BLACK, ORANGE} from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import ToggleGroup from 'ui-library/ToggleGroup';

import {SavedSearchPermissionsGraphQL} from '../../../graphql-fragments/search/__generated/SavedSearchPermissionsGraphQL';

interface OwnerCollaboratorRowProps {
  owner: Owner;
  ownerInfo: OwnerInfo;
  permissions: SavedSearchPermissionsGraphQL;
  permissionLevelToDataPolicyId: {[level in SavedSearchPermissionLevel]: string};
  onChangeOwnerInfo: (newOwnerInfo: OwnerInfo) => void;
  memberPermissionLevel: SavedSearchPermissionLevel | 'OWNER' | null;
  memberWarning: boolean;
  noBorder: boolean;
}

export function OwnerCollaboratorRow(props: OwnerCollaboratorRowProps): JSX.Element {
  const displayMembers = props.owner.type === ModelTypeConstants.ORGANIZATION && props.memberPermissionLevel;
  return (
    <div
      className={css(
        styles.collaborator,
        !displayMembers && styles.soloOwner,
        !props.noBorder && styles.border
      )}
    >
      <Collaborator
        key={props.owner.id}
        collaborator={props.owner}
        permissionLevel="OWNER"
        onChangePermissionLevel={null}
        permissions={props.permissions}
        onRemove={null}
        warning={false}
      />
      {displayMembers && (
        <Collaborator
          key={`${props.owner.id}_members`}
          collaborator={props.owner}
          permissionLevel={props.memberPermissionLevel!}
          onChangePermissionLevel={(newPermissionLevel) => {
            props.onChangeOwnerInfo({
              ...props.ownerInfo,
              memberDataPolicyId: props.permissionLevelToDataPolicyId[newPermissionLevel],
            });
          }}
          permissions={props.permissions}
          onRemove={() => {
            props.onChangeOwnerInfo({
              ...props.ownerInfo,
              memberDataPolicyId: null,
            });
          }}
          warning={props.memberWarning}
          isMemberRow={true}
        />
      )}
    </div>
  );
}

interface CollaboratorRowProps {
  collaborator: DataPolicyCollaborator;
  permissionLevel: SavedSearchPermissionLevel;
  onChangeCollaborators: (newCollaborators: Array<DataPolicyCollaborator>) => void;
  collaborators: Array<DataPolicyCollaborator>;
  permissionLevelToDataPolicyId: {[level in SavedSearchPermissionLevel]: string};
  permissions: SavedSearchPermissionsGraphQL;
  memberWarning: boolean;
  adminWarning: boolean;
  adminPermissionLevel: SavedSearchPermissionLevel;
  noBorder: boolean;
}

export function CollaboratorRow(props: CollaboratorRowProps): JSX.Element {
  return (
    <div className={css(styles.collaborator, !props.noBorder && styles.border)}>
      <Collaborator
        key={props.collaborator.id}
        collaborator={props.collaborator}
        permissionLevel={props.permissionLevel}
        onChangePermissionLevel={(newPermissionLevel) => {
          props.onChangeCollaborators(
            props.collaborators.map((c) =>
              c.id === props.collaborator.id
                ? setDataPolicyId(c, {
                    dataPolicyId: props.permissionLevelToDataPolicyId[newPermissionLevel],
                  })
                : c
            )
          );
        }}
        permissions={props.permissions}
        onRemove={() => {
          props.onChangeCollaborators(props.collaborators.filter((c) => c.id !== props.collaborator.id));
        }}
        warning={props.memberWarning}
      />
      {!isActorCollaborator(props.collaborator) && (
        <Collaborator
          key={`${props.collaborator.id}_admin`}
          collaborator={props.collaborator}
          permissionLevel={props.adminPermissionLevel}
          onChangePermissionLevel={(newPermissionLevel) => {
            props.onChangeCollaborators(
              props.collaborators.map((c) =>
                c.id === props.collaborator.id
                  ? setDataPolicyId(c, {
                      adminDataPolicyId: props.permissionLevelToDataPolicyId[newPermissionLevel],
                    })
                  : c
              )
            );
          }}
          permissions={props.permissions}
          onRemove={() => {
            props.onChangeCollaborators(props.collaborators.filter((c) => c.id !== props.collaborator.id));
          }}
          warning={props.adminWarning}
          isAdminRow={true}
        />
      )}
    </div>
  );
}

interface SavedSearchCollaboratorsProps {
  collaborator: DataPolicyCollaborator | Owner;
  /** 'OWNER' is a sentinel that indicates this collaborator is the owner and has no permission level. */
  permissionLevel: SavedSearchPermissionLevel | 'OWNER';
  /** Null if never changeable. */
  onChangePermissionLevel: ((newLevel: SavedSearchPermissionLevel) => void) | null;
  /** True if the current user can add or remove collaborators with the given level. */
  permissions: SavedSearchPermissionsGraphQL;
  /** Null if never removable. */
  onRemove: (() => void) | null;
  /** True if user doesn't have permissions to access all referenced api_ids in filters */
  warning: boolean;
  /** True if this collaborator is specifying the members of a group */
  isMemberRow?: boolean;
  /** True if this collaborator is specifying the admins of a group */
  isAdminRow?: boolean;
}

export function Collaborator({
  collaborator,
  permissionLevel,
  onChangePermissionLevel,
  permissions,
  onRemove,
  warning,
  isMemberRow,
  isAdminRow,
}: SavedSearchCollaboratorsProps): JSX.Element {
  const canEdit = {
    [SavedSearchPermissionLevel.VIEW]: permissions.canAddRemoveViewers,
    [SavedSearchPermissionLevel.EDIT]: permissions.canAddRemoveEditors,
    [SavedSearchPermissionLevel.ADMIN]: permissions.canAddRemoveAdmins,
    OWNER: false,
  }[permissionLevel];

  return (
    <>
      <div className={css(styles.avatarAndWarning)}>
        {isMemberRow || isAdminRow ? (
          <div className={css(styles.wrapper)}>
            <BenchlingIcon className={css(styles.subItem)} icon="sub-item" />
            <span className={css(styles.name)}>{getCustomName(isMemberRow, isAdminRow, collaborator)}</span>
          </div>
        ) : (
          <AvatarWithName
            avatarInfo={collaborator}
            size={AvatarSize.SMALL}
            shape={
              isUser(collaborator as {type?: EntityType | undefined})
                ? AvatarShape.CIRCLE
                : AvatarShape.SQUARE
            }
            showTooltip={false}
            wrapName={true}
          />
        )}
        {warning && (
          <Tooltip
            title={
              isUser(collaborator as {type?: EntityType | undefined})
                ? 'This user doesn’t have permission to view a schema used in this search. They will be not be able to run it'
                : 'This group does not have permission to view a schema used in this search. Some users may be unable to run it.'
            }
            placement={TooltipPlacement.BOTTOM}
          >
            <BenchlingIcon className={css(styles.icon)} icon="warning" />
          </Tooltip>
        )}
      </div>

      {permissionLevel === 'OWNER' ? (
        <div>Owner</div>
      ) : (
        <ToggleGroup<SavedSearchPermissionLevel>
          options={[
            {
              id: SavedSearchPermissionLevel.VIEW,
              buttonProps: {text: 'View', disabled: !(canEdit && permissions.canAddRemoveViewers)},
            },
            {
              id: SavedSearchPermissionLevel.EDIT,
              buttonProps: {text: 'Edit', disabled: !(canEdit && permissions.canAddRemoveEditors)},
            },
            {
              id: SavedSearchPermissionLevel.ADMIN,
              buttonProps: {text: 'Admin', disabled: !(canEdit && permissions.canAddRemoveAdmins)},
            },
          ]}
          selection={permissionLevel}
          disabled={onChangePermissionLevel == null}
          onChangeSelection={onChangePermissionLevel!}
          size={ButtonSize.MEDIUM}
        />
      )}

      {isAdminRow ? (
        <div />
      ) : (
        <Button
          className={css(styles.trashcan)}
          buttonStyle={ButtonStyle.CIRCLE_ICON}
          icon="format-delete"
          size={ButtonSize.MEDIUM}
          disabled={onRemove == null || !canEdit}
          onClick={onRemove!}
          dataTestAttrs={{
            component: 'SavedSearchCollaborator',
            element: 'deleteButton',
            key: collaborator.id,
          }}
        />
      )}
    </>
  );
}

function getCustomName(
  isMemberRow: boolean | undefined,
  isAdminRow: boolean | undefined,
  avatarInfo: HasAvatar
): string {
  const displayString = getPartyDisplayString(avatarInfo);
  if (isMemberRow) {
    return `Members of ${displayString}`;
  } else if (isAdminRow) {
    return `Admins of ${displayString}`;
  } else {
    return '';
  }
}

const styles = StyleSheet.create({
  collaborator: {
    display: 'grid',
    gap: '0.2em 1em',
    alignItems: 'center',
    gridTemplateColumns: '1fr auto auto',
  },
  border: {
    borderBottom: `1px solid ${GRAY}`,
    paddingBottom: '0.5em',
  },
  soloOwner: {
    gridTemplateColumns: '1fr 0.95fr auto',
  },
  avatarAndWarning: {
    display: 'flex',
    alignItems: 'center',
  },
  trashcan: {
    marginLeft: '-10px',
  },
  icon: {
    flexShrink: 0,
    fontSize: '16px',
    color: ORANGE,
    marginLeft: '-8px',
  },
  name: {
    color: NEAR_BLACK,
    fontFamily: FONT_FAMILY_PROXIMA,
    fontSize: 13,
    lineHeight: '20px',
    fontStyle: 'italic',
    overflowWrap: 'anywhere',
  },
  wrapper: {
    alignItems: 'center',
    display: 'flex',
    marginRight: 14,
  },
  subItem: {
    fontSize: '16px',
    rotate: '180deg',
    marginRight: '5px',
    marginLeft: '14px',
    paddingTop: '2px',
    color: GRAY_DARKER,
  },
});
